
import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';

export default function NaloxoneKits() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';

  useSEO({
    title: 'Free Naloxone Kits Thornhill | Narcan Richmond Hill, Vaughan | Opioid Overdose Prevention',
    description: 'Get free naloxone kits (Narcan) in Thornhill serving Richmond Hill, Vaughan, and North York. Life-saving opioid overdose reversal medication available without prescription. Walk-in naloxone training and education. Call (905) 709-9985 or visit our pharmacy for free naloxone kits today.',
    keywords: 'free naloxone kits Thornhill, Narcan Thornhill, naloxone Richmond Hill, free Narcan Vaughan, opioid overdose prevention Thornhill, naloxone training North York, free naloxone pharmacy near me, Narcan nasal spray Thornhill, naloxone kit pickup Richmond Hill, overdose reversal medication Thornhill, naloxone without prescription',
    canonical: `${siteUrl}/naloxone-kits`,
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "name": "Thornhill Drug-Mart Free Naloxone Kit Distribution",
      "description": "Free naloxone kit distribution center in Thornhill, Ontario providing life-saving opioid overdose reversal medication without prescription. Walk-in naloxone training and education available. Serving Thornhill, Richmond Hill, Vaughan, North York, and Greater Toronto Area.",
      "url": `${siteUrl}/naloxone-kits`,
      "telephone": "+1-905-709-9985",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "8108 Yonge St Unit 3-4",
        "addressLocality": "Thornhill",
        "addressRegion": "ON",
        "postalCode": "L4J 1W4",
        "addressCountry": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "43.8184",
        "longitude": "-79.4278"
      },
      "medicalSpecialty": ["Emergency Medicine", "Pharmacy"],
      "availableService": {
        "@type": "MedicalTherapy",
        "name": "Free Naloxone Kit Distribution and Training",
        "description": "Free naloxone kits (Narcan) available without prescription for opioid overdose prevention and reversal. Includes comprehensive training on recognizing overdose symptoms and proper naloxone administration"
      }
    }
  });

  const faqs = [
    {
      question: 'What is naloxone and how does it work?',
      answer: 'Naloxone (brand name Narcan) is a life-saving medication that rapidly reverses opioid overdose. It works by blocking opioid receptors in the brain, quickly restoring normal breathing in someone who has overdosed on opioids like heroin, fentanyl, oxycodone, or other prescription painkillers. Naloxone has no effect on people who haven\'t taken opioids and cannot be used to get high.'
    },
    {
      question: 'Are naloxone kits really free?',
      answer: 'Yes! Naloxone kits are completely free at our Thornhill pharmacy through the Ontario Naloxone Program for Pharmacies. You don\'t need a prescription, health card, or any identification to receive a free naloxone kit. We provide kits to anyone who may witness or experience an opioid overdose, including people who use opioids, family members, friends, and community members.'
    },
    {
      question: 'Do I need a prescription to get a naloxone kit?',
      answer: 'No prescription is required! Naloxone kits are available without a prescription at our Thornhill pharmacy. Simply walk in during our business hours and ask for a free naloxone kit. Our trained pharmacists will provide the kit and offer brief training on how to recognize an overdose and use naloxone properly.'
    },
    {
      question: 'How do I use a naloxone kit?',
      answer: 'Our naloxone kits include easy-to-follow instructions. For nasal spray: 1) Check for signs of overdose (unresponsive, slow/no breathing, blue lips). 2) Call 911 immediately. 3) Give rescue breaths if trained. 4) Spray naloxone into one nostril. 5) Continue rescue breaths. 6) If no response after 2-3 minutes, give second dose. Our pharmacists provide hands-on training when you pick up your kit.'
    },
    {
      question: 'What are the signs of an opioid overdose?',
      answer: 'Signs of opioid overdose include: person is unresponsive or unconscious, breathing is very slow or stopped, lips and fingernails are blue or purple, skin is cold and clammy, pupils are very small (pinpoint), choking or gurgling sounds. If you see these signs, call 911 immediately and administer naloxone if available. Time is critical in overdose situations.'
    },
    {
      question: 'Will I get in trouble for calling 911 during an overdose?',
      answer: 'No. Ontario\'s Good Samaritan Drug Overdose Act provides legal protection for people who call 911 during an overdose. The law protects you from charges for simple drug possession, possession of drug paraphernalia, and breach of conditions related to simple possession. Saving a life is the priority, and you should always call 911 during an overdose emergency.'
    },
    {
      question: 'Can naloxone harm someone if they haven\'t taken opioids?',
      answer: 'No, naloxone will not harm someone who hasn\'t taken opioids. It only works if opioids are present in the body. If you\'re unsure whether someone has overdosed on opioids, it\'s safe to give naloxone. The potential benefit of saving a life far outweighs any minimal risk. When in doubt, administer naloxone and call 911.'
    },
    {
      question: 'How many naloxone kits can I get?',
      answer: 'You can get multiple naloxone kits from our Thornhill pharmacy. We encourage people to keep kits in different locations (home, car, workplace) and to replace used kits immediately. There\'s no limit on how many free kits you can receive. If you\'ve used a kit or it\'s expired, come back anytime for a replacement at no cost.'
    }
  ];

  const kitContents = [
    {
      icon: 'ri-syringe-line',
      title: 'Naloxone Nasal Spray',
      description: 'Pre-filled nasal spray devices for easy administration'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Instructions',
      description: 'Step-by-step guide for recognizing and responding to overdose'
    },
    {
      icon: 'ri-phone-line',
      title: 'Emergency Info',
      description: 'Quick reference card with 911 and crisis line numbers'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Protective Gloves',
      description: 'Disposable gloves for safe administration'
    }
  ];

  const whoNeeds = [
    {
      icon: 'ri-user-heart-line',
      title: 'People Who Use Opioids',
      description: 'Anyone using prescription opioids or street drugs should carry naloxone'
    },
    {
      icon: 'ri-parent-line',
      title: 'Family & Friends',
      description: 'Loved ones of people who use opioids should have naloxone available'
    },
    {
      icon: 'ri-community-line',
      title: 'Community Members',
      description: 'Anyone who may witness an overdose in their community or workplace'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Healthcare Workers',
      description: 'Medical professionals and first responders working with at-risk populations'
    },
    {
      icon: 'ri-building-line',
      title: 'Businesses',
      description: 'Workplaces, shelters, and organizations serving vulnerable populations'
    },
    {
      icon: 'ri-team-line',
      title: 'Support Workers',
      description: 'Social workers, counselors, and harm reduction staff'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Visit Our Pharmacy',
      description: 'Walk into our Thornhill location during business hours',
      icon: 'ri-store-3-line'
    },
    {
      number: '02',
      title: 'Request a Kit',
      description: 'Ask our pharmacist for a free naloxone kit - no ID or prescription needed',
      icon: 'ri-hand-heart-line'
    },
    {
      number: '03',
      title: 'Receive Training',
      description: 'Get brief training on overdose recognition and naloxone use',
      icon: 'ri-graduation-cap-line'
    },
    {
      number: '04',
      title: 'Take It Home',
      description: 'Keep your kit accessible and check expiry dates regularly',
      icon: 'ri-home-heart-line'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[80vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=life-saving%20naloxone%20nasal%20spray%20kit%20on%20clean%20white%20surface%2C%20modern%20pharmacy%20setting%20with%20teal%20accents%2C%20bright%20professional%20lighting%2C%20emergency%20medical%20supplies%2C%20opioid%20overdose%20prevention%20tools%2C%20organized%20healthcare%20workspace%2C%20compassionate%20harm%20reduction%20environment%2C%20safe%20community%20health%20setting&width=1920&height=1000&seq=naloxone-hero-001&orientation=landscape"
          alt="Free naloxone kits available in Thornhill"
          title="Free Naloxone Kits - Thornhill Drug-Mart"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

        <div className="relative z-10 w-full px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center mb-6 sm:mb-8" aria-label="Breadcrumb">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center text-white/80 hover:text-white transition-colors cursor-pointer"
                  aria-label="Go to homepage"
                >
                  <i className="ri-home-4-line text-base sm:text-lg"></i>
                  <span className="ml-2 text-xs sm:text-sm font-medium">Home</span>
                </button>
                <i className="ri-arrow-right-s-line text-white/60 text-base sm:text-lg"></i>
                <button
                  onClick={() => navigate('/addiction-services')}
                  className="text-white/80 hover:text-white text-xs sm:text-sm font-medium transition-colors cursor-pointer"
                >
                  Addiction Services
                </button>
                <i className="ri-arrow-right-s-line text-white/60 text-base sm:text-lg"></i>
                <span className="text-primary-400 text-xs sm:text-sm font-semibold">Free Naloxone Kits</span>
              </div>
            </nav>

            {/* Badge */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="inline-flex items-center space-x-2 bg-red-600/20 backdrop-blur-md px-3 py-1.5 rounded-full">
                <span className="text-red-300 text-xs font-semibold uppercase tracking-wide">Overdose Prevention</span>
              </div>
            </div>

            {/* Content */}
            <header className="text-center mb-8 sm:mb-10">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Free Naloxone Kits (Narcan)
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-10 max-w-4xl mx-auto leading-relaxed">
                Get <strong>free naloxone kits</strong> in <strong>Thornhill</strong> for life-saving <strong>opioid overdose prevention</strong>. No prescription needed. Walk-in training available. Serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>.
              </p>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
                <a
                  href="tel:9057099985"
                  className="bg-red-600 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-red-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Call about free naloxone kits"
                >
                  <i className="ri-phone-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">(905) 709-9985</span>
                  <span className="sm:hidden">Call Now</span>
                </a>
                <a
                  href="/location"
                  className="bg-white text-gray-900 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Visit for free naloxone kit"
                >
                  <i className="ri-map-pin-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Get Your Kit</span>
                  <span className="sm:hidden">Location</span>
                </a>
              </div>
            </header>
          </div>
        </div>
      </section>

      {/* What is Naloxone Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                What is <strong>Naloxone</strong>?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Naloxone</strong> (brand name <strong>Narcan</strong>) is a life-saving medication that rapidly reverses <strong>opioid overdose</strong>. It works by blocking opioid receptors in the brain, quickly restoring normal breathing in someone who has overdosed on opioids like heroin, fentanyl, oxycodone, or other prescription painkillers.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                Our <strong>Thornhill pharmacy</strong> provides <strong>free naloxone kits</strong> through the Ontario Naloxone Program. No prescription, health card, or identification required. We offer walk-in training on recognizing overdose symptoms and proper <strong>naloxone administration</strong>.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, <strong>North York</strong>, and the Greater Toronto Area, we're committed to <strong>opioid overdose prevention</strong> and saving lives in our community.
              </p>
            </div>
            <div className="w-full h-96 lg:h-full rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=compassionate%20pharmacist%20demonstrating%20naloxone%20nasal%20spray%20use%2C%20professional%20training%20session%20in%20modern%20pharmacy%20with%20teal%20accents%2C%20warm%20educational%20environment%2C%20harm%20reduction%20counseling%2C%20life-saving%20medication%20instruction%2C%20clean%20bright%20clinical%20space%2C%20supportive%20community%20health%20setting&width=800&height=600&seq=naloxone-what-is-001&orientation=landscape"
                alt="Naloxone training in Thornhill pharmacy"
                title="Free Naloxone Training - Thornhill Drug-Mart"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who Needs Naloxone Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Who Should Have <strong>Naloxone</strong>?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Anyone who may witness or experience an <strong>opioid overdose</strong> should carry <strong>naloxone</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {whoNeeds.map((item, index) => (
              <article 
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-red-600 text-white rounded-lg mb-4">
                  <i className={`${item.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Kit Contents Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              What's in a <strong>Naloxone Kit</strong>?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need to respond to an <strong>opioid overdose</strong> emergency
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {kitContents.map((item, index) => (
              <article 
                key={index}
                className="bg-gradient-to-br from-red-50 to-white rounded-xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-red-600 text-white rounded-lg mb-4">
                  <i className={`${item.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How to Get Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-primary-600 to-cyan-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              How to Get Your <strong>Free Naloxone Kit</strong>
            </h2>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl mx-auto">
              Simple steps to get <strong>free naloxone</strong> at our <strong>Thornhill pharmacy</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <article 
                key={index}
                className="relative bg-white/10 backdrop-blur-md rounded-xl p-6 hover:bg-white/20 transition-all"
              >
                <div className="absolute top-4 right-4 text-5xl font-bold text-white/20">
                  {step.number}
                </div>
                <div className="relative">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-white text-primary-600 rounded-lg mb-4">
                    <i className={`${step.icon} text-2xl sm:text-3xl`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-sm sm:text-base text-white/80 leading-relaxed">{step.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Frequently Asked <span className="text-red-600">Questions</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Common questions about <strong>naloxone kits</strong> in <strong>Thornhill</strong>
            </p>
          </header>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article 
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  aria-expanded={expandedFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 
                    className="text-base sm:text-lg font-semibold text-gray-900 pr-4"
                    itemProp="name"
                  >
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-red-600 text-2xl flex-shrink-0`}></i>
                </button>
                {expandedFaq === index && (
                  <div 
                    id={`faq-answer-${index}`}
                    className="px-6 pb-5"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
            Get Your <strong>Free Naloxone Kit</strong> Today
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-8 sm:mb-10 leading-relaxed">
            Visit our <strong>Thornhill pharmacy</strong> for <strong>free naloxone kits</strong> and training. No prescription or ID required. Help save lives in <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>. Walk in anytime during business hours.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href="tel:9057099985"
              className="bg-red-600 text-white px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-red-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="Call about naloxone kits"
            >
              <i className="ri-phone-line mr-2"></i>
              (905) 709-9985
            </a>
            <a
              href="/location"
              className="bg-gray-900 text-white px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-800 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="Visit for naloxone kit"
            >
              <i className="ri-map-pin-line mr-2"></i>
              Get Directions
            </a>
          </div>

          <div className="mt-8 sm:mt-10 flex items-center justify-center space-x-2 text-gray-600">
            <i className="ri-shield-check-line text-xl"></i>
            <p className="text-sm sm:text-base">100% Free • No Prescription • No ID Required</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
